import { APP_SLUGS, APP_SLUG_TO_LABEL } from '../constants/appSlugs';
import {
  DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES,
  STUDENT_INFORMATION_FIELD_DEFS,
  getStudentInformationFieldDef,
  normalizeStudentEnabledAttributes
} from '../constants/studentInformationFields';
import {
  getEndedStudentStatuses,
  getGradeLevelOptions,
  getStudentStatusOptions
} from '../constants/studentInformationOptions';

/** CSV header aliases → student_info field keys (beyond exact key / label match). */
const STUDENT_FIELD_HEADER_ALIASES = {
  track_period_id: [
    'track',
    'track period',
    'schedule track',
    'track_period_id',
    'track period id'
  ],
  primary_pathway: [
    'pathway',
    'primary pathway',
    'primary_pathway',
    'concentration',
    'primary concentration'
  ],
  grade_level: ['grade', 'grade level', 'grade_level'],
  vendor_groups_periods_id: [
    'vendor',
    'vendor partner',
    'vendor_groups_periods_id',
    'fa track'
  ]
};

export function resolveHeaderToStudentFieldKey(header) {
  const raw = String(header ?? '').trim().toLowerCase();
  const withSpaces = raw.replace(/_/g, ' ');

  for (const def of STUDENT_INFORMATION_FIELD_DEFS) {
    if (def.key === raw || def.key.replace(/_/g, ' ') === withSpaces) {
      return def.key;
    }
    if (def.label.toLowerCase() === withSpaces) {
      return def.key;
    }
  }

  for (const [fieldKey, aliases] of Object.entries(STUDENT_FIELD_HEADER_ALIASES)) {
    if (aliases.includes(raw) || aliases.includes(withSpaces)) {
      return fieldKey;
    }
  }

  return null;
}

function getStudentInfoEnabledAttributes(studentApp) {
  if (!studentApp) return [];
  if (Array.isArray(studentApp.metadata?.enabled_attributes)) {
    return normalizeStudentEnabledAttributes(studentApp.metadata.enabled_attributes);
  }
  return [...DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES];
}

/**
 * Map extra CSV columns to app fields using app_assignments on the group-period.
 * Only called when extra columns are present.
 */
export function resolveExtraColumnAppMappings(extraColumns, assignedApps) {
  const mappings = [];
  const errors = [];
  const studentApp = (assignedApps || []).find(
    (app) => app.slug === APP_SLUGS.STUDENT_INFORMATION
  );
  const enabledAttrs = getStudentInfoEnabledAttributes(studentApp);
  const studentMeta = studentApp?.metadata || {};

  extraColumns.forEach(({ raw, index }) => {
    const fieldKey = resolveHeaderToStudentFieldKey(raw);
    if (!fieldKey) {
      errors.push({
        row: 1,
        message: `Unknown column "${raw}". Extra columns must match a field from an app assigned to this group-period.`
      });
      return;
    }

    if (!studentApp) {
      errors.push({
        row: 1,
        message: `Column "${raw}" requires the Student Information app on this group-period.`
      });
      return;
    }

    if (!enabledAttrs.includes(fieldKey)) {
      const label = getStudentInformationFieldDef(fieldKey)?.label || fieldKey;
      errors.push({
        row: 1,
        message: `"${raw}" maps to ${label}, but that field is not enabled on the Student Information assignment for this group-period.`
      });
      return;
    }

    mappings.push({
      csvHeader: raw,
      columnIndex: index,
      appSlug: APP_SLUGS.STUDENT_INFORMATION,
      fieldKey,
      fieldLabel: getStudentInformationFieldDef(fieldKey)?.label || fieldKey,
      appLabel: APP_SLUG_TO_LABEL[APP_SLUGS.STUDENT_INFORMATION] || 'Student Information',
      appDisplayName:
        studentMeta.container_label_override ||
        APP_SLUG_TO_LABEL[APP_SLUGS.STUDENT_INFORMATION] ||
        'Student Information',
      metadata: studentMeta
    });
  });

  return { mappings, errors };
}

export function formatExtraColumnMappingsForUi(mappings) {
  return (mappings || []).map(
    ({ csvHeader, appSlug, fieldKey, fieldLabel, appLabel, appDisplayName }) => ({
      csvHeader,
      appSlug,
      fieldKey,
      fieldLabel,
      appLabel,
      appDisplayName
    })
  );
}

export function groupExtraColumnMappingsByApp(mappings) {
  const groups = [];
  const indexBySlug = new Map();

  for (const mapping of mappings || []) {
    const slug = mapping.appSlug;
    if (!indexBySlug.has(slug)) {
      const group = {
        appSlug: slug,
        appLabel: mapping.appLabel,
        appDisplayName: mapping.appDisplayName
      };
      indexBySlug.set(slug, groups.length);
      groups.push({ ...group, mappings: [] });
    }
    groups[indexBySlug.get(slug)].mappings.push(mapping);
  }

  return groups;
}

function parseBooleanCsvValue(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return { ok: true, value: null };
  if (['true', 'yes', 'y', '1'].includes(normalized)) return { ok: true, value: true };
  if (['false', 'no', 'n', '0'].includes(normalized)) return { ok: true, value: false };
  return { ok: false, message: 'Use true/false, yes/no, or 1/0.' };
}

function parseDateCsvValue(value) {
  const normalized = String(value ?? '').trim();
  if (!normalized) return { ok: true, value: null };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return { ok: false, message: 'Use date format YYYY-MM-DD.' };
  }
  const parsed = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return { ok: false, message: 'Invalid date.' };
  }
  return { ok: true, value: normalized };
}

/**
 * Normalize track titles for matching: "Track A", "A", "track-a" → comparable forms.
 * Letter-only values like "A" match periods titled "Track A".
 */
export function normalizeTrackMatchKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^track[\s\-_]*/i, '')
    .replace(/[\s\-_]+/g, '');
}

/**
 * Resolve a CSV track value to a scheduler track period id.
 * Accepts full titles ("Track A") or short codes ("A").
 */
export function resolveTrackPeriodFromCsvValue(rawValue, trackOptions = []) {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed) {
    return { ok: true, value: null, matchedLabel: null, sheetValue: null, matchMethod: null };
  }

  const options = Array.isArray(trackOptions) ? trackOptions : [];
  if (options.length === 0) {
    return {
      ok: false,
      message:
        'No schedule tracks are available for this organization. Add track periods in Schedule Builder first.'
    };
  }

  const byId = options.find((opt) => String(opt.id) === trimmed);
  if (byId) {
    const label = byId.label || byId.name || trimmed;
    return {
      ok: true,
      value: String(byId.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'id'
    };
  }

  const lower = trimmed.toLowerCase();
  const exactTitle = options.find((opt) => {
    const name = String(opt.name || opt.label || '').trim().toLowerCase();
    const label = String(opt.label || '').trim().toLowerCase();
    return name === lower || label === lower;
  });
  if (exactTitle) {
    const label = exactTitle.label || exactTitle.name || trimmed;
    return {
      ok: true,
      value: String(exactTitle.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'exact_title'
    };
  }

  const csvKey = normalizeTrackMatchKey(trimmed);
  if (!csvKey) {
    return { ok: false, message: 'Track value is empty after normalizing.' };
  }

  const letterMatches = options.filter((opt) => {
    const nameKey = normalizeTrackMatchKey(opt.name || opt.label || '');
    return nameKey === csvKey;
  });

  if (letterMatches.length === 1) {
    const hit = letterMatches[0];
    const label = hit.label || hit.name || trimmed;
    return {
      ok: true,
      value: String(hit.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'letter_code'
    };
  }
  if (letterMatches.length > 1) {
    const titles = letterMatches.map((o) => o.label || o.name || o.id).join(', ');
    return {
      ok: false,
      message: `Track "${trimmed}" matches multiple periods (${titles}). Use the full period title.`
    };
  }

  const titles = options.map((o) => o.label || o.name || o.id).filter(Boolean);
  const hint =
    titles.length > 0
      ? ` Expected a letter (e.g. A → Track A) or one of: ${titles.slice(0, 8).join(', ')}${
          titles.length > 8 ? '…' : ''
        }.`
      : '';
  return { ok: false, message: `Unknown track "${trimmed}".${hint}` };
}

function normalizePathwayMatchKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Resolve a CSV pathway / concentration title or id to taxonomy.id (match by taxonomy.title). */
export function resolvePathwayFromCsvValue(rawValue, pathwayOptions = []) {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed) {
    return { ok: true, value: null, matchedLabel: null, sheetValue: null, matchMethod: null };
  }

  const options = Array.isArray(pathwayOptions) ? pathwayOptions : [];
  if (options.length === 0) {
    return {
      ok: false,
      message:
        'No primary pathways (taxonomy concentrations) are available for this organization. Add concentrations in Taxonomy, or uncheck the Primary pathway column.'
    };
  }

  const byId = options.find((opt) => String(opt.id) === trimmed);
  if (byId) {
    const label = byId.label || byId.title || byId.name || trimmed;
    return {
      ok: true,
      value: String(byId.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'id'
    };
  }

  const key = normalizePathwayMatchKey(trimmed);
  const titleMatches = options.filter((opt) => {
    const title = normalizePathwayMatchKey(opt.title || opt.name || '');
    const label = normalizePathwayMatchKey(opt.label || '');
    const shortTitle = normalizePathwayMatchKey(opt.short_title || '');
    return title === key || label === key || shortTitle === key;
  });

  if (titleMatches.length === 1) {
    const hit = titleMatches[0];
    const label = hit.label || hit.title || hit.name || trimmed;
    return {
      ok: true,
      value: String(hit.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'exact_title'
    };
  }
  if (titleMatches.length > 1) {
    const titles = titleMatches.map((o) => o.label || o.title || o.id).join(', ');
    return {
      ok: false,
      message: `Primary pathway "${trimmed}" matches multiple concentrations (${titles}). Use a more specific title or the taxonomy id.`
    };
  }

  const titles = options.map((o) => o.label || o.title || o.name || o.id).filter(Boolean);
  const hint =
    titles.length > 0
      ? ` Expected a taxonomy.title such as: ${titles.slice(0, 8).join(', ')}${
          titles.length > 8 ? '…' : ''
        }.`
      : '';
  return { ok: false, message: `Unknown primary pathway "${trimmed}".${hint}` };
}

function normalizeVendorMatchKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]+/g, '');
}

/**
 * Resolve a CSV vendor value (e.g. FA01, FA16) to a vendor partner groups_periods id.
 * Matches primarily on team group name; prefers the most recent period when several GP rows share a name.
 */
export function resolveVendorGpFromCsvValue(rawValue, vendorOptions = []) {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed) {
    return { ok: true, value: null, matchedLabel: null, sheetValue: null, matchMethod: null };
  }

  const options = Array.isArray(vendorOptions) ? vendorOptions : [];
  if (options.length === 0) {
    return {
      ok: false,
      message:
        'No vendor partner groups are available for this organization. Ensure team groups (e.g. FA01) exist under this org, or uncheck the Vendor column.'
    };
  }

  const byId = options.find((opt) => String(opt.id) === trimmed);
  if (byId) {
    const label = byId.group_name || byId.label || trimmed;
    return {
      ok: true,
      value: String(byId.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'id'
    };
  }

  const csvKey = normalizeVendorMatchKey(trimmed);
  if (!csvKey) {
    return { ok: false, message: 'Vendor value is empty after normalizing.' };
  }

  const nameMatches = options.filter((opt) => {
    const groupKey = normalizeVendorMatchKey(opt.group_name || '');
    const labelKey = normalizeVendorMatchKey(opt.label || '');
    return groupKey === csvKey || labelKey === csvKey;
  });

  if (nameMatches.length === 0) {
    const names = [
      ...new Set(options.map((o) => o.group_name || o.label || o.id).filter(Boolean))
    ];
    const hint =
      names.length > 0
        ? ` Expected a vendor group name such as: ${names.slice(0, 10).join(', ')}${
            names.length > 10 ? '…' : ''
          }.`
        : '';
    return { ok: false, message: `Unknown vendor "${trimmed}".${hint}` };
  }

  if (nameMatches.length === 1) {
    const hit = nameMatches[0];
    const label = hit.group_name || hit.label || trimmed;
    return {
      ok: true,
      value: String(hit.id),
      matchedLabel: label,
      sheetValue: trimmed,
      matchMethod: 'group_name'
    };
  }

  // Several GP rows for the same team name (different periods) — prefer newest period start.
  const sorted = [...nameMatches].sort((a, b) => {
    const aTime = a.period_start ? new Date(a.period_start).getTime() : 0;
    const bTime = b.period_start ? new Date(b.period_start).getTime() : 0;
    return bTime - aTime;
  });
  const hit = sorted[0];
  const label = hit.group_name || hit.label || trimmed;
  return {
    ok: true,
    value: String(hit.id),
    matchedLabel: label,
    sheetValue: trimmed,
    matchMethod: 'group_name_latest_period'
  };
}

export function validateStudentInfoFieldValue(fieldKey, value, metadata, fieldOptions = {}) {
  const trimmed = String(value ?? '').trim();
  const label = getStudentInformationFieldDef(fieldKey)?.label || fieldKey;

  if (!trimmed) {
    return { ok: true, value: null };
  }

  switch (fieldKey) {
    case 'grade_level': {
      const options = getGradeLevelOptions(metadata);
      if (options.length > 0 && !options.includes(trimmed)) {
        return {
          ok: false,
          message: `${label} must be one of: ${options.join(', ')}.`
        };
      }
      return { ok: true, value: trimmed };
    }
    case 'track_period_id': {
      const resolved = resolveTrackPeriodFromCsvValue(
        trimmed,
        fieldOptions.track_period_id || []
      );
      if (!resolved.ok) {
        return { ok: false, message: `${label}: ${resolved.message}` };
      }
      return {
        ok: true,
        value: resolved.value,
        matchedLabel: resolved.matchedLabel || null,
        sheetValue: resolved.sheetValue || trimmed,
        matchMethod: resolved.matchMethod || null
      };
    }
    case 'primary_pathway': {
      const resolved = resolvePathwayFromCsvValue(
        trimmed,
        fieldOptions.primary_pathway || []
      );
      if (!resolved.ok) {
        return { ok: false, message: `${label}: ${resolved.message}` };
      }
      return {
        ok: true,
        value: resolved.value,
        matchedLabel: resolved.matchedLabel || null,
        sheetValue: resolved.sheetValue || trimmed,
        matchMethod: resolved.matchMethod || null
      };
    }
    case 'vendor_groups_periods_id': {
      const resolved = resolveVendorGpFromCsvValue(
        trimmed,
        fieldOptions.vendor_groups_periods_id || []
      );
      if (!resolved.ok) {
        return { ok: false, message: `${label}: ${resolved.message}` };
      }
      return {
        ok: true,
        value: resolved.value,
        matchedLabel: resolved.matchedLabel || null,
        sheetValue: resolved.sheetValue || trimmed,
        matchMethod: resolved.matchMethod || null
      };
    }
    case 'status': {
      const options = getStudentStatusOptions(metadata);
      if (!options.includes(trimmed)) {
        return {
          ok: false,
          message: `${label} must be one of: ${options.join(', ')}.`
        };
      }
      return { ok: true, value: trimmed };
    }
    case 'enrollment_date':
    case 'exit_date': {
      const parsed = parseDateCsvValue(trimmed);
      if (!parsed.ok) {
        return { ok: false, message: `${label}: ${parsed.message}` };
      }
      return { ok: true, value: parsed.value };
    }
    case 'previously_enrolled': {
      const parsed = parseBooleanCsvValue(trimmed);
      if (!parsed.ok) {
        return { ok: false, message: `${label}: ${parsed.message}` };
      }
      return { ok: true, value: parsed.value };
    }
    case 'transfer_credits': {
      const n = parseFloat(trimmed);
      if (Number.isNaN(n)) {
        return { ok: false, message: `${label} must be a number.` };
      }
      return { ok: true, value: n };
    }
    default:
      return { ok: true, value: trimmed };
  }
}

export function validateRowStudentInfoFields(studentData, metadata) {
  const status = studentData.status;
  if (!status) return null;

  const statusOptions = getStudentStatusOptions(metadata);
  const endedStatuses = getEndedStudentStatuses(metadata, statusOptions);
  if (endedStatuses.includes(status) && !studentData.exit_date) {
    return `exit_date is required when status is "${status}".`;
  }

  return null;
}

export async function fetchAssignedAppsForGroupsPeriod(groupsPeriodsId, token) {
  if (!groupsPeriodsId) {
    throw new Error('A group-period is required to map extra CSV columns.');
  }

  const response = await fetch(`/api/apps/assigned-to-groups-periods/${groupsPeriodsId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Could not load app assignments for this group-period.');
  }
  return data.data || [];
}

export async function fetchTrackOptionsForGroupsPeriod(groupsPeriodsId, token) {
  if (!groupsPeriodsId) {
    throw new Error('A group-period is required to resolve schedule tracks.');
  }

  const params = new URLSearchParams({ groups_periods_id: String(groupsPeriodsId) });
  const response = await fetch(`/api/student-info/track-options-for-gp?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Could not load schedule track options.');
  }
  return data.data?.track_period_id || [];
}

export async function fetchPathwayOptionsForGroupsPeriod(groupsPeriodsId, token) {
  if (!groupsPeriodsId) {
    throw new Error('A group-period is required to resolve primary pathways.');
  }

  const params = new URLSearchParams({ groups_periods_id: String(groupsPeriodsId) });
  const response = await fetch(`/api/student-info/pathway-options-for-gp?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Could not load primary pathway options.');
  }
  return data.data?.primary_pathway || [];
}

export async function fetchVendorOptionsForGroupsPeriod(
  groupsPeriodsId,
  token,
  assignmentMetadata = {}
) {
  if (!groupsPeriodsId) {
    throw new Error('A group-period is required to resolve vendor partners.');
  }

  const params = new URLSearchParams({ groups_periods_id: String(groupsPeriodsId) });
  if (assignmentMetadata && Object.keys(assignmentMetadata).length > 0) {
    params.set('assignment_metadata', JSON.stringify(assignmentMetadata));
  }
  const response = await fetch(`/api/student-info/vendor-options-for-gp?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Could not load vendor partner options.');
  }
  return data.data?.vendor_groups_periods_id || [];
}
